'use client'

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useDeleteProducts } from '@/lib/api/products'
import { useToast } from '@/components/ui/use-toast'

interface BulkDeleteProductsModalProps {
    productIds: string[]
    onClose: () => void
    onSuccess?: () => void
}

/**
 * Modal component that confirms bulk product deletion
 */
export const BulkDeleteProductsModal = ({ productIds, onClose, onSuccess }: BulkDeleteProductsModalProps) => {
    const { t } = useTranslation('products')
    const { t: tCommon } = useTranslation('common')
    const { toast } = useToast()
    const deleteProducts = useDeleteProducts()

    const count = productIds.length

    const handleDelete = async () => {
        if (productIds.length === 0) return

        try {
            await deleteProducts.mutateAsync({ productIds })

            toast({
                title: t('bulkDeleteSuccess'),
                description: t('productsDeleted', { count }),
            })

            onSuccess?.()
            onClose()
        } catch (error) {
            toast({
                title: tCommon('error'),
                description: t('bulkDeleteError'),
                variant: 'destructive',
            })
        }
    }

    return (
        <AlertDialog open={true} onOpenChange={onClose}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('bulkDeleteTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>{t('bulkDeleteConfirmation', { count })}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={deleteProducts.isPending}>
                        {tCommon('cancel')}
                    </Button>
                    <Button variant="destructive" onClick={handleDelete} disabled={deleteProducts.isPending}>
                        {deleteProducts.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {tCommon('delete')}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

